export type ApiOk<T> = { ok: true; status: number; data: T };
export type ApiErr = { ok: false; status: number; error: string };
export type ApiResult<T> = ApiOk<T> | ApiErr;

export async function api<T>(
  method: string,
  path: string,
  opts: {
    body?: unknown;
    editToken?: string | null;
    adminToken?: string | null;
    keepalive?: boolean;
    parse: (v: unknown) => T | null;
  },
): Promise<ApiResult<T>> {
  const headers: Record<string, string> = {};
  const hasBody = method === "POST" || method === "PUT" || method === "PATCH";
  if (hasBody) headers["content-type"] = "application/json";
  if (opts.editToken) headers["x-edit-token"] = opts.editToken;
  if (opts.adminToken) headers["x-admin-token"] = opts.adminToken;
  const init: RequestInit = { method, headers };
  if (hasBody) init.body = JSON.stringify(opts.body ?? {});
  if (opts.keepalive) init.keepalive = true;
  try {
    const res = await fetch(path, init);
    let data: unknown = null;
    const text = await res.text();
    if (text) {
      try {
        data = JSON.parse(text) as unknown;
      } catch {
        data = { error: "invalid response" };
      }
    }
    if (!res.ok) {
      const error =
        data !== null && typeof data === "object" && "error" in data && typeof (data as { error: unknown }).error === "string"
          ? (data as { error: string }).error
          : "Something went wrong. Try again.";
      return { ok: false, status: res.status, error };
    }
    const parsed = opts.parse(data);
    if (parsed === null) {
      return { ok: false, status: res.status, error: "Something went wrong. Try again." };
    }
    return { ok: true, status: res.status, data: parsed };
  } catch {
    return { ok: false, status: 0, error: "Could not reach the server." };
  }
}
